import { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';

import { OperationalGuide } from '@/components/common/OperationalGuide';
import { format, parseISO } from 'date-fns';
import { startOfDay } from 'date-fns';
import { Plus, Pencil } from 'lucide-react';

import noHarvestsPng from '@/assets/illustrations/empty/no-harvests.png';

import { useHuntStore } from '@/stores/hunt-store';
import { useHarvestStore } from '@/stores/harvest-store';
import { useWaypointStore } from '@/stores/waypoint-store';
import { Button } from '@/components/common/Button';
import { PhotoGallery } from '@/components/photos/PhotoGallery';
import { PhotoPicker } from '@/components/photos/PhotoPicker';
import { SitCard } from '@/components/hunts/SitCard';
import { HarvestTrophyCard } from '@/components/hunts/HarvestTrophyCard';
import { JournalFeed } from '@/components/hunts/JournalFeed';
// M-09 (Defect Batch M): LogSitSheet extracted into a shared component so the
// Dashboard active-hunt card can mount the same sheet inline.
import { LogSitSheet } from '@/components/hunts/LogSitSheet';
import { getPhotosForHunt, deleteHuntPhoto } from '@/services/photo-service';
import type { PhotoRecord } from '@/types/photo';
import type { HarvestRecord } from '@/types';


// ---------------------------------------------------------------------------
// Hunt Detail Page
// ---------------------------------------------------------------------------

export function HuntDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { currentHunt, sits, isLoading, loadHunt, loadSitsForHunt, deleteSit } =
    useHuntStore();
  const { records: harvestRecords, loadForHunt: loadHarvests, deleteRecord: deleteHarvestRecord } =
    useHarvestStore();
  const { waypoints: locations, loadWaypoints: loadLocations } = useWaypointStore();

  const [showLogSitSheet, setShowLogSitSheet] = useState(false);
  const [editingSitId, setEditingSitId] = useState<string | null>(null);
  const [photos, setPhotos] = useState<PhotoRecord[]>([]);
  // State tracks which harvest is pending confirm-delete (value unused — only setter needed)
  const [_confirmDeleteHarvest, setConfirmDeleteHarvest] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    void loadHunt(id);
    void loadSitsForHunt(id);
    void loadHarvests(id);
    getPhotosForHunt(id)
      .then((photos) => setPhotos(photos.filter((p) => !p.harvestId && !p.sitId)))
      .catch((err) => console.error('[HuntDetailPage] Failed to load photos:', err));
  }, [id, loadHunt, loadSitsForHunt, loadHarvests]);

  // Load locations for the hunt's property
  useEffect(() => {
    if (currentHunt?.propertyId) {
      void loadLocations(currentHunt.propertyId);
    }
  }, [currentHunt?.propertyId, loadLocations]);

  const handleDeleteSit = useCallback(
    async (sitId: string) => {
      try {
        await deleteSit(sitId);
      } catch (err) {
        console.error('[HuntDetailPage] Failed to delete sit:', err);
      }
    },
    [deleteSit],
  );

  const handleDeleteHarvest = useCallback(
    async (harvestId: string) => {
      try {
        await deleteHarvestRecord(harvestId);
        setConfirmDeleteHarvest(null);
      } catch (err) {
        console.error('[HuntDetailPage] Failed to delete harvest:', err);
      }
    },
    [deleteHarvestRecord],
  );

  const handleDeletePhoto = useCallback(
    async (photoId: string) => {
      if (!id) return;
      try {
        await deleteHuntPhoto(photoId, id);
        setPhotos((prev) => prev.filter((p) => p.id !== photoId));
      } catch (err) {
        console.error('[HuntDetailPage] Failed to delete photo:', err);
      }
    },
    [id],
  );

  if (isLoading || !currentHunt) {
    return (
      <div className="flex flex-col gap-4 animate-pulse">
        <div className="h-8 bg-[var(--color-elevated)] rounded w-2/3" />
        <div className="h-24 bg-[var(--color-elevated)] rounded" />
        <div className="h-40 bg-[var(--color-elevated)] rounded" />
      </div>
    );
  }

  const hunt = currentHunt;

  const startLabel = format(parseISO(hunt.startDate), 'MMM d');
  const endLabel = format(parseISO(hunt.endDate), 'MMM d, yyyy');
  const dateRange =
    hunt.startDate === hunt.endDate
      ? startLabel + ', ' + format(parseISO(hunt.endDate), 'yyyy')
      : `${startLabel} – ${endLabel}`;

  const companionsText =
    hunt.companions.length > 0 ? `With ${hunt.companions.join(', ')}` : null;

  const isOpenerReached = startOfDay(new Date()) >= startOfDay(parseISO(hunt.startDate));

  // Check if this is an "active" (ongoing) hunt for context
  const _isActive =
    isOpenerReached && startOfDay(new Date()) <= startOfDay(parseISO(hunt.endDate));

  return (
    /*
      Gate condition: !isLoading && currentHunt != null

      useEffect alignment: the rebuild plan's "side-effect useEffects gate on the same condition"
      criterion is RedeemBetaPage-shaped (auth-gated mutation); HuntDetailPage's useEffects are
      read-only data loaders gated on `if (!id) return;` — the gate they need is "preconditions
      present" not "auth resolved", and that gate is already in place; this interpretation maps
      the criterion to non-auth pages and the three parallel Wave 1 retrofits should match this
      style for any non-auth pages they cover.

      Layout neutrality: the wrapper uses Tailwind v4's `contents` utility (display: contents)
      so it sits in the DOM for Playwright querying but does not participate in the CSS box tree,
      preserving the inner element's direct-child relationship to its parent and protecting iOS
      Safari PWA viewport-recalc defenses like `min-w-0 overflow-x-hidden`.

      data-status intentionally omitted: HuntDetailPage's gate is data-loader state, not authStatus;
      the sentinel's conditional presence is the entire contract. See ADR-035 / RULE-194..199.
    */
    <div className="contents" data-testid="route-ready">
      {/* hypothesis (fallback artifacts) — verify on device: page-level min-w-0 + overflow-x-hidden defends against iOS Safari PWA viewport-scale recalculation when a descendant overflows (L-03) */}
      <div className="flex flex-col gap-6 min-w-0 overflow-x-hidden" data-testid="hunt-detail-page">
      {/* Back nav */}
      <Link
        to="/hunt"
        className="self-start text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors text-body-sm flex items-center gap-1"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="15 18 9 12 15 6" />
        </svg>
        Hunt Journal
      </Link>

      {/* ── 1. Hunt Header ────────────────────────────────────── */}
      <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-col gap-1 min-w-0">
            <h1
              className="font-display text-display-sm text-[var(--color-text-display)]"
              data-testid="hunt-name"
            >
              {hunt.name}
            </h1>
            <p className="text-body-sm text-[var(--color-text-secondary)]">{dateRange}</p>
            {companionsText && (
              <p
                className="text-body-xs text-[var(--color-text-muted)]"
                data-testid="companions-text"
              >
                {companionsText}
              </p>
            )}
          </div>
          {/* hypothesis (fallback artifacts) — verify on device: flex-wrap allows action buttons to wrap to a second row on narrow viewports rather than forcing horizontal overflow (L-03) */}
          <div className="flex flex-wrap gap-2 shrink-0 justify-end">
            <button
              type="button"
              onClick={() => navigate(`/hunt/${hunt.id}/harvest/new`)}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-body-xs text-[var(--color-accent)] hover:text-[var(--color-accent-hover)] hover:bg-[var(--color-elevated)] border border-[var(--color-accent)]/40 transition-colors"
              data-testid="log-harvest-btn"
            >
              Log Harvest
            </button>
            <Link
              to={`/hunt/${hunt.id}/edit`}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-body-xs text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-elevated)] border border-[var(--color-border)] transition-colors"
              data-testid="edit-hunt-link"
            >
              <Pencil size={13} />
              Edit
            </Link>
          </div>
        </div>
      </div>

      {/* ── 2. Journal Feed ───────────────────────────────────── */}
      <JournalFeed huntId={id} />

      {/* ── 3. Sits Section ───────────────────────────────────── */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <p
            className="field-label"
            data-testid="hunt-section-header-sits"
          >
            {`SITS · ${sits.length}`}
          </p>
        </div>

        {sits.length === 0 ? (
          <div
            className="flex flex-col items-center justify-center py-10 px-6 text-center"
            data-testid="sits-empty-state"
          >
            <p className="font-display text-display-sm text-[var(--color-text-primary)] mb-2">
              No Sits Logged Yet
            </p>
            <p className="text-body-sm text-[var(--color-text-muted)] mb-4">
              Add a sit to track when and where you hunted.
            </p>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setShowLogSitSheet(true)}
              data-testid="log-first-sit-btn"
            >
              <Plus size={14} className="mr-1" />
              Log a Sit
            </Button>
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-2" data-testid="sits-list">
              {sits.map((sit) => {
                const location = locations.find((l) => l.id === sit.waypointId);
                const locationCategory = location?.type
                  ? location.type.replace(/_/g, ' ').toUpperCase()
                  : undefined;
                return (
                  <SitCard
                    key={sit.id}
                    sit={sit}
                    locationName={location?.name ?? 'Unknown Location'}
                    locationCategory={locationCategory}
                    huntId={hunt.id}
                    onDelete={(sitId) => void handleDeleteSit(sitId)}
                    onEdit={(sitId) => setEditingSitId(sitId)}
                  />
                );
              })}
            </div>

            <Button
              type="button"
              variant="ghost"
              onClick={() => setShowLogSitSheet(true)}
              className="self-start"
              data-testid="log-sit-btn"
            >
              <Plus size={14} className="mr-1" />
              Log a Sit
            </Button>
          </>
        )}
      </div>

      {/* ── 4. Harvests Section ──────────────────────────────── */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <p
            className="field-label"
            data-testid="hunt-section-header-harvests"
          >
            {`HARVESTS · ${harvestRecords.filter((r) => r.huntId === hunt.id).length}`}
          </p>
        </div>

        {harvestRecords.filter((r) => r.huntId === hunt.id).length === 0 ? (
          // M-05: drop the redundant "Go to Hunt Journal" CTA (we are already on it).
          // Show a "Log a Harvest" CTA only when at least one sit has been logged;
          // with zero sits, render no CTA.
          sits.length === 0 ? (
            <OperationalGuide
              illustration={noHarvestsPng}
              heading="No Trophies Yet"
              subtext="Your successful hunts will appear here"
              compact
              data-guide-id="hunt-detail-no-harvests"
            />
          ) : (
            <OperationalGuide
              illustration={noHarvestsPng}
              heading="No Trophies Yet"
              subtext="Your successful hunts will appear here"
              ctaLabel="Log a Harvest"
              onCtaClick={() => navigate(`/hunt/${hunt.id}/harvest/new`)}
              compact
              data-guide-id="hunt-detail-no-harvests"
            />
          )
        ) : (
          <div className="flex flex-col gap-2" data-testid="harvest-records-list">
            {harvestRecords
              .filter((r) => r.huntId === hunt.id)
              .map((record: HarvestRecord) => {
                const location = locations.find((l) => l.id === record.waypointId);
                return (
                  <HarvestTrophyCard
                    key={record.id}
                    record={record}
                    locationName={location?.name}
                    huntId={hunt.id}
                    onDelete={(harvestId) => void handleDeleteHarvest(harvestId)}
                  />
                );
              })}
          </div>
        )}
      </div>

      {/* ── 5. Photos Section ─────────────────────────────────── */}
      <div className="flex flex-col gap-3">
        <p
          className="field-label"
          data-testid="hunt-section-header-photos"
        >
          {`PHOTOS · ${photos.length}`}
        </p>

        {photos.length > 0 && (
          <PhotoGallery
            photos={photos}
            onDelete={(photoId) => void handleDeletePhoto(photoId)}
            editable
            getSitContext={(photo) => {
              if (!photo.sitId) return undefined;
              const sit = sits.find((s) => s.id === photo.sitId);
              if (!sit) return undefined;
              const location = locations.find((l) => l.id === sit.waypointId);
              const periodLabel =
                sit.period === 'morning'
                  ? 'Morning'
                  : sit.period === 'evening'
                    ? 'Evening'
                    : 'All Day';
              return `${location?.name ?? 'Unknown'} · ${periodLabel}`;
            }}
          />
        )}

        <PhotoPicker
          entityType="hunt"
          entityId={hunt.id}
          existingCount={photos.length}
          onPhotosAdded={(newPhotos) => setPhotos((prev) => [...prev, ...newPhotos])}
          disabled={false}
        />
      </div>

      {/* Log Sit Bottom Sheet (create mode) */}
      {showLogSitSheet && (
        <LogSitSheet
          huntId={hunt.id}
          propertyId={hunt.propertyId}
          onClose={() => {
            setShowLogSitSheet(false);
            // Re-fetch harvests: a harvest may have been created from the sit's
            // "Log Harvest" link while this sheet was open (J-1).
            void loadHarvests(hunt.id);
          }}
        />
      )}

      {/* Edit Sit Bottom Sheet */}
      {editingSitId && (
        <LogSitSheet
          huntId={hunt.id}
          propertyId={hunt.propertyId}
          existingSit={sits.find((s) => s.id === editingSitId)}
          onClose={() => {
            setEditingSitId(null);
            // Re-fetch harvests: a harvest may have been created from the sit's
            // "Log Harvest" link while this sheet was open (J-1).
            void loadHarvests(hunt.id);
          }}
        />
      )}

      {/* Delete hunt (accessible via Edit page) */}
      <div className="pb-8" />
      </div>
    </div>
  );
}
